import React from 'react';
import { Link } from 'react-router-dom';
import { Code2, Trophy, ShoppingCart, TrendingUp, Search, Rocket, DollarSign, ExternalLink, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { PatreonTier } from '../components/PatreonTier';
import { SEO, aboutPageJsonLd, breadcrumbs } from '../components/SEO';

const portfolioProjects = [
  {
    id: 'ates-it',
    title: 'AtesITSolutions.com',
    description: 'Creative digital marketing solutions that ensure investments lead to tangible results.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
    url: 'https://atesitsolutions.com',
    tags: ['Business', 'Web Design']
  },
  {
    id: 'emelkedik',
    title: 'EmelKedik.com',
    description: 'Dynamic platform showcasing YouTube vlogs and Ghost-powered blog for fresh content delivery.',
    image: '/assets/projects/emelkedik.com-project.png',
    url: 'https://emelkedik.com',
    tags: ['Business', 'Web Design']
  },
  {
    id: 'istanbulbarbershop',
    title: 'Istanbul Barbershop',
    description: 'Stylish Turkish barbershop combining traditional techniques with modern styling in Warsaw.',
    image: 'https://media.swipepages.com/2024/10/670eb19c08e40f00114a38b3/istanbul_logo-750.webp',
    url: 'https://www.istanbulbarbershop.pl/',
    tags: ['Local Business', 'Web Design']
  },
  {
    id: 'multioutlet',
    title: 'Multioutlet.com.pl',
    description: 'Warsaw outlet store offering watches, bags, clothing and furniture at lowest prices.',
    image: 'https://multioutlet.com.pl/wp-content/uploads/2025/01/home-zegarek-w-multioutlet-2025.jpg',
    url: 'https://www.multioutlet.com.pl/',
    tags: ['E-commerce', 'Retail']
  },
  {
    id: 'enenskebab',
    title: "Enen's Kebab",
    description: 'Authentic Turkish kebab restaurant in Poland with fresh ingredients and traditional recipes.',
    image: 'https://images.unsplash.com/photo-1599487488170-d11ec9c172f0?auto=format&fit=crop&q=80&w=800',
    url: 'https://www.enenskebab.pl/',
    tags: ['Restaurant', 'Web Design']
  }
];

export const About: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="pt-24 pb-20">
      <SEO 
        title="About"
        description="Learn about Hüseyin Cem Ateş - full-stack developer specializing in web applications, mobile solutions, and digital products."
        canonical="/about"
        jsonLd={{ "@context": "https://schema.org", "@graph": [aboutPageJsonLd, breadcrumbs.about] }}
      />
      {/* Hero Section */}
      <section className="mb-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-semibold mb-4">
            {t('nav', 'about')}
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            {t('about', 'hero')} {t('about', 'subtitle')}
          </p>
        </div>
      </section>

      {/* Experience Section */}
      <section className="mb-20" id="experience">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-semibold mb-10 text-center">{t('about', 'experience.title')}</h2>
          <div className="space-y-8">
            <div className="border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Code2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <h3 className="font-medium mb-1">{t('about', 'experience.developer.title')}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{t('about', 'experience.developer.position')}</p>
                  <ul className="text-muted-foreground text-sm space-y-1.5">
                    <li>• {t('about', 'experience.developer.point1')}</li>
                    <li>• {t('about', 'experience.developer.point2')}</li>
                    <li>• {t('about', 'experience.developer.point3')}</li>
                    <li>• {t('about', 'experience.developer.point4')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <ShoppingCart className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <h3 className="font-medium mb-1">{t('about', 'experience.ecommerce.title')}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{t('about', 'experience.ecommerce.position')}</p>
                  <ul className="text-muted-foreground text-sm space-y-1.5">
                    <li>• {t('about', 'experience.ecommerce.point1')}</li>
                    <li>• {t('about', 'experience.ecommerce.point2')}</li>
                    <li>• {t('about', 'experience.ecommerce.point3')}</li>
                    <li>• {t('about', 'experience.ecommerce.point4')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Search className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <h3 className="font-medium mb-1">{t('about', 'experience.marketing.title')}</h3>
                  <p className="text-muted-foreground text-sm mb-3">{t('about', 'experience.marketing.position')}</p>
                  <ul className="text-muted-foreground text-sm space-y-1.5">
                    <li>• {t('about', 'experience.marketing.point1')}</li>
                    <li>• {t('about', 'experience.marketing.point2')}</li>
                    <li>• {t('about', 'experience.marketing.point3')}</li>
                    <li>• {t('about', 'experience.marketing.point4')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="mb-20" id="portfolio">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-semibold mb-4 text-center">Client Work</h2>
          <p className="text-muted-foreground text-center mb-10 max-w-lg mx-auto">
            Websites and platforms I've built for clients and personal brands.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioProjects.map((project) => (
              <a
                key={project.id}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block border border-border rounded-xl overflow-hidden hover:border-foreground/20 transition-all"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{project.title}</h3>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <p className="text-muted-foreground text-sm mb-3">{project.description}</p>
                  <div className="flex gap-2">
                    {project.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-1 bg-muted rounded-md text-muted-foreground">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </a>
            ))}
          </div>
          
          {/* View All Projects CTA */}
          <div className="mt-10 text-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
            >
              View All Projects
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="mb-20 bg-muted/30 py-20" id="skills">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold mb-10 text-center">{t('skills', 'title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-5">
              <Code2 className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-3">{t('skills', 'development')}</h3>
              <ul className="space-y-1.5 text-muted-foreground text-sm">
                <li>React / Next.js</li>
                <li>Node.js / Express</li>
                <li>{t('skills', 'mobile')}</li>
                <li>{t('skills', 'cloud')}</li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-5">
              <ShoppingCart className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-3">{t('skills', 'ecommerce')}</h3>
              <ul className="space-y-1.5 text-muted-foreground text-sm">
                <li>Amazon FBA/FBM</li>
                <li>Shopify/WooCommerce</li>
                <li>eBay/Allegro</li>
                <li>Dropshipping</li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-5">
              <Search className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-3">{t('skills', 'marketing')}</h3>
              <ul className="space-y-1.5 text-muted-foreground text-sm">
                <li>{t('skills', 'seo')}</li>
                <li>{t('skills', 'content')}</li>
                <li>{t('skills', 'ppc')}</li>
                <li>{t('skills', 'funnels')}</li>
              </ul>
            </div>

            <div className="bg-card border border-border rounded-lg p-5">
              <TrendingUp className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-3">{t('skills', 'business')}</h3>
              <ul className="space-y-1.5 text-muted-foreground text-sm">
                <li>{t('skills', 'revenue')}</li>
                <li>{t('skills', 'market')}</li>
                <li>{t('skills', 'strategy')}</li>
                <li>{t('skills', 'team')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="mb-20" id="achievements">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-semibold mb-10 text-center">{t('achievements', 'title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border border-border rounded-lg p-5">
              <DollarSign className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-2">{t('achievements', 'businesstitle')}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('achievements', 'businesstext')}
              </p>
            </div>

            <div className="border border-border rounded-lg p-5">
              <Trophy className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-2">{t('achievements', 'technicaltitle')}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('achievements', 'technicaltext')}
              </p>
            </div>

            <div className="border border-border rounded-lg p-5">
              <Rocket className="w-5 h-5 text-muted-foreground mb-3" />
              <h3 className="font-medium mb-2">{t('achievements', 'leadertitle')}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('achievements', 'leadertext')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Patreon Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold mb-3">{t('patreon', 'support')}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              {t('patreon', 'title')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <PatreonTier
              title={t('patreon', 'tiers.flame.title')}
              price="$1"
              description={t('patreon', 'tiers.flame.description')}
              icon="flame"
              link="https://www.patreon.com/cemates/membership"
            />
            <PatreonTier
              title={t('patreon', 'tiers.heart.title')}
              price="$4.99"
              description={t('patreon', 'tiers.heart.description')}
              icon="heart"
              link="https://www.patreon.com/cemates/membership"
            />
            <PatreonTier
              title={t('patreon', 'tiers.crown.title')}
              price="$9.99"
              description={t('patreon', 'tiers.crown.description')}
              icon="crown"
              link="https://www.patreon.com/cemates/membership"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
